import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, doc, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { thoughtsService } from '../services/thoughtsService';
import BubbleForm from './BubbleForm';
import './AdminDashboard.css';

const AdminDashboard = ({ onLogout, user }) => {
  const [bubbles, setBubbles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBubble, setEditingBubble] = useState(null);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [copiedLink, setCopiedLink] = useState(null);

  useEffect(() => {
    loadBubbles();
  }, []);

  const loadBubbles = async () => {
    try {
      setLoading(true);
      // Load bubbles ordered by creation date (latest first)
      const bubblesRef = collection(db, 'thoughts');
      const q = query(bubblesRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const bubblesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setBubbles(bubblesData);
    } catch (error) {
      console.error('Error loading bubbles:', error);
      setError('Failed to load bubbles. Please check your Firestore permissions.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddBubble = () => {
    setEditingBubble(null);
    setShowForm(true);
  };

  const handleEditBubble = (bubble) => {
    setEditingBubble(bubble);
    setShowForm(true);
  };

  const handleDeleteBubble = (bubble) => {
    setShowDeleteConfirm(bubble);
  };

  const confirmDelete = async () => {
    if (!showDeleteConfirm) return;
    
    try {
      await thoughtsService.deleteThought(showDeleteConfirm.id);
      setBubbles(bubbles.filter(bubble => bubble.id !== showDeleteConfirm.id));
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting bubble:', error);
      setError('Failed to delete bubble');
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(null);
  };

  const handleFormSubmit = async (bubbleData) => {
    try {
      if (editingBubble) {
        // Update existing bubble
        const bubbleRef = doc(db, 'thoughts', editingBubble.id);
        await updateDoc(bubbleRef, bubbleData);
        setBubbles(bubbles.map(bubble => 
          bubble.id === editingBubble.id ? { ...bubble, ...bubbleData } : bubble
        ));
      } else {
        // Add new bubble with timestamp
        const bubbleWithTimestamp = {
          ...bubbleData,
          createdAt: new Date().toISOString()
        };
        const docRef = await addDoc(collection(db, 'thoughts'), bubbleWithTimestamp);
        // Add new bubble to the beginning of the list (latest first)
        setBubbles([{ id: docRef.id, ...bubbleWithTimestamp }, ...bubbles]);
      }
      setShowForm(false);
      setEditingBubble(null);
    } catch (error) {
      console.error('Error saving bubble:', error);
      setError('Failed to save bubble. Please check your Firestore permissions.');
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingBubble(null);
  };

  const getThoughtUrl = (bubbleId) => {
    // Get the current domain or use the production URL
    const baseUrl = window.location.origin;
    return `${baseUrl}/thought/${bubbleId}`;
  };

  const copyThoughtLink = async (bubbleId) => {
    try {
      const thoughtUrl = getThoughtUrl(bubbleId);
      await navigator.clipboard.writeText(thoughtUrl);
      setCopiedLink(bubbleId);
      // Reset the copied state after 2 seconds
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = getThoughtUrl(bubbleId);
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedLink(bubbleId);
      setTimeout(() => setCopiedLink(null), 2000);
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="loading">Loading bubbles...</div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <div className="admin-title">
          <h1>Admin Dashboard</h1>
          {user && (
            <p className="user-info">
              Welcome, {user.email} | 
              <button onClick={onLogout} className="logout-btn">Logout</button>
            </p>
          )}
        </div>
        <div className="admin-actions">
          <button onClick={handleAddBubble} className="btn-primary">
            Add New Bubble
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError('')} className="close-error">×</button>
        </div>
      )}

      <div className="bubbles-list">
        <h2>Bubbles ({bubbles.length})</h2>
        {bubbles.length === 0 ? (
          <div className="no-bubbles">
            <p>No bubbles found. Add your first bubble!</p>
          </div>
        ) : (
          <div className="bubbles-grid">
            {bubbles.map(bubble => (
              <div key={bubble.id} className="bubble-card">
                <div className="bubble-header">
                  <h3>{bubble.thought || bubble.text || 'Untitled'}</h3>
                  <div className="bubble-actions">
                    <button 
                      onClick={() => handleEditBubble(bubble)}
                      className="btn-edit"
                    >
                      Edit
                    </button>
                    <button 
                      onClick={() => handleDeleteBubble(bubble)}
                      className="btn-delete"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <div className="bubble-content">
                  {bubble.imageUrl && (
                    <div className="bubble-image">
                      <img 
                        src={bubble.imageUrl} 
                        alt={`${bubble.thought || bubble.text} - ${bubble.author}`}
                        className="bubble-preview-image"
                      />
                    </div>
                  )}
                  
                  <p><strong>Thought Link:</strong> 
                    <a 
                      href={getThoughtUrl(bubble.id)} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="thought-link"
                    >
                      {getThoughtUrl(bubble.id)}
                    </a>
                    <button 
                      onClick={() => copyThoughtLink(bubble.id)}
                      className={`copy-link-btn ${copiedLink === bubble.id ? 'copied' : ''}`}
                      title={copiedLink === bubble.id ? 'Copied!' : 'Copy link'}
                    >
                      {copiedLink === bubble.id ? '✓' : '📋'}
                    </button>
                  </p>

                  <p><strong>Author:</strong> {bubble.author || 'Unknown'}</p>
                  <p><strong>Application:</strong> {bubble.application || 'N/A'}</p>
                  {bubble.summary && (
                    <p><strong>Summary:</strong> {bubble.summary}</p>
                  )}
                  {bubble.url && (
                    <p><strong>URL:</strong> 
                      <a href={bubble.url} target="_blank" rel="noopener noreferrer">
                        {bubble.url}
                      </a>
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <BubbleForm
          bubble={editingBubble}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
        />
      )}

      {showDeleteConfirm && (
        <div className="delete-confirmation-overlay">
          <div className="delete-confirmation-dialog">
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete this bubble?</p>
            <div className="bubble-preview">
              <strong>{showDeleteConfirm.thought || showDeleteConfirm.text || 'Untitled'}</strong>
              {showDeleteConfirm.author && (
                <p>by {showDeleteConfirm.author}</p>
              )}
            </div>
            <div className="confirmation-actions">
              <button onClick={cancelDelete} className="btn-cancel">
                Cancel
              </button>
              <button onClick={confirmDelete} className="btn-confirm-delete">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
